/**
 * @module engine3d/models/utility
 * @summary 3D models for Valves & Instruments, Utilities, and the annotation
 *   symbols (zone box, text label, process arrow, title block).
 * @description
 *   Valves and instruments are small, so they are modelled *generously* — a
 *   valve gets a proper flanged body, a bonnet, a stem and a handwheel or
 *   actuator. At the scale a whole line is viewed, that silhouette is the only
 *   thing distinguishing a gate valve from a pressure gauge, so it has to be
 *   right even though the part is tiny.
 *
 *   Annotations are treated differently: they are drawing furniture, not plant,
 *   so they render as flat translucent ground markings rather than as objects
 *   with height — which keeps them from cluttering the 3D scene.
 */
import {
  box,
  boxCentered,
  cylinder,
  cylinderX,
  cylinderZ,
  extrude,
  controlBox,
  dome,
  filletPath,
  flange,
  frustum,
  merge,
  motorUnit,
  nozzle,
  p,
  pm,
  rectFoot,
  rotateX,
  rotateZ,
  roundFoot,
  skid,
  sphere,
  torusArc,
  translate,
  tube,
  v3,
  type Builder,
  type Mesh,
  type Model3,
} from './kit';

// ─── Valves ──────────────────────────────────────────────────────────────

/**
 * The valve archetype: a flanged body on the process line (running along +X),
 * a bonnet, a stem, and a topworks — handwheel, lever, actuator or solenoid.
 */
function valve(
  bodyKind: 'gate' | 'ball' | 'check' | 'globe' | 'butterfly',
  top: 'wheel' | 'lever' | 'actuator' | 'solenoid' | 'none',
): Builder {
  return ({ w, h, d }): Model3 => {
    const r = Math.min(w, d) * 0.24;
    const cy = h * 0.42;
    const bodyParts: Mesh[] = [
      translate(cylinderX(r, w * 0.9, 14), 0, cy, 0),
      translate(rotateZ(flange(r * 1.7, 2, 14), Math.PI / 2), -w * 0.45, cy, 0),
      translate(rotateZ(flange(r * 1.7, 2, 14), -Math.PI / 2), w * 0.45, cy, 0),
    ];
    if (bodyKind === 'gate') bodyParts.push(translate(boxCentered(r * 1.5, r * 2.2, r * 2.4), 0, cy, 0));
    else if (bodyKind === 'ball') bodyParts.push(translate(sphere(r * 1.5, 16, 10), 0, cy, 0));
    else if (bodyKind === 'globe') bodyParts.push(translate(sphere(r * 1.6, 16, 10), 0, cy, 0));
    else if (bodyKind === 'check')
      bodyParts.push(translate(rotateZ(frustum(r * 1.5, r * 1.1, w * 0.3, { seg: 14 }), -Math.PI / 2), -w * 0.15, cy, 0));
    else bodyParts.push(translate(cylinderX(r * 1.4, w * 0.16, 16), 0, cy, 0));

    const stemH = h * 0.3;
    const topY = cy + r * 1.6;
    const topworks: { mesh: Mesh; mat: 'accent' | 'machine' | 'brass' }[] = [];
    if (top !== 'none') {
      topworks.push({ mesh: translate(cylinder(r * 0.9, r * 0.9, { seg: 12 }), 0, topY, 0), mat: 'machine' });
      topworks.push({ mesh: translate(cylinder(r * 0.28, stemH, { seg: 8 }), 0, topY + r * 0.9, 0), mat: 'brass' });
    }
    if (top === 'wheel')
      topworks.push({
        mesh: merge(
          translate(rotateX(torusArc(r * 1.7, r * 0.22, 0, Math.PI * 2, 14, 6), -Math.PI / 2), 0, topY + stemH, 0),
          translate(rotateZ(cylinderX(r * 0.14, r * 3.4, 6), 0), 0, topY + stemH, 0),
        ),
        mat: 'accent',
      });
    else if (top === 'lever')
      topworks.push({ mesh: translate(boxCentered(r * 3.4, r * 0.5, r * 0.6), r * 1.2, topY + stemH, 0), mat: 'accent' });
    else if (top === 'actuator')
      topworks.push({
        mesh: merge(
          translate(cylinder(r * 1.5, h * 0.28, { seg: 16 }), 0, topY + stemH, 0),
          translate(flange(r * 1.7, 1.6, 16), 0, topY + stemH + h * 0.28, 0),
        ),
        mat: 'accent',
      });
    else if (top === 'solenoid')
      topworks.push({
        mesh: merge(
          translate(boxCentered(r * 1.6, h * 0.24, r * 1.6), 0, topY + stemH + h * 0.12, 0),
          translate(cylinder(r * 0.3, h * 0.08, { seg: 8 }), 0, topY + stemH + h * 0.24, 0),
        ),
        mat: 'machine',
      });

    // With no topworks there is no stem and no handwheel to allow for, so the
    // body IS the top. Counting them anyway put a check valve's height — and
    // with it the port a vertical run docks to — 16 units above the metal.
    const totalH =
      top === 'none'
        ? topY
        : topY + stemH + (top === 'actuator' || top === 'solenoid' ? h * 0.32 : r * 1.8);
    return {
      parts: [
        p(translate(cylinder(r * 0.5, cy - r * 1.2, { seg: 8 }), 0, 0, 0), 'galv'),
        p(merge(...bodyParts), bodyKind === 'butterfly' ? 'stainless' : 'cast'),
        ...topworks.map((t) => p(t.mesh, t.mat)),
      ],
      height: totalH,
      ports: {
        W: { p: v3(-w * 0.48, cy, 0), n: v3(-1, 0, 0) },
        E: { p: v3(w * 0.48, cy, 0), n: v3(1, 0, 0) },
        N: { p: v3(0, totalH, 0), n: v3(0, 1, 0) },
        S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
      },
      footprint: rectFoot(w * 0.8, d * 0.7),
    };
  };
}

export const gatevalve = valve('gate', 'wheel');
export const ballvalve = valve('ball', 'lever');
export const checkvalve = valve('check', 'none');
export const ctrlvalve = valve('globe', 'actuator');
export const butterflyvalve = valve('butterfly', 'lever');
export const solenoidvalve = valve('gate', 'solenoid');
export const prv = valve('globe', 'wheel');

/**
 * The in-line instrument archetype: a small tapping off the pipe carrying a
 * dial, a probe head or a transmitter.
 */
function instrument(kind: 'dial' | 'probe' | 'meter' | 'switch'): Builder {
  return ({ w, h, d }): Model3 => {
    const r = Math.min(w, d) * 0.18;
    const cy = h * 0.36;
    const stemH = h * 0.26;
    const headY = cy + r + stemH;
    const head: Mesh =
      kind === 'dial'
        ? merge(
            translate(cylinderZ(Math.min(w, d) * 0.38, Math.min(w, d) * 0.2, 18), 0, headY, -Math.min(w, d) * 0.1),
            translate(cylinderZ(Math.min(w, d) * 0.42, 2, 18), 0, headY, Math.min(w, d) * 0.1),
          )
        : kind === 'probe'
          ? merge(
              translate(boxCentered(w * 0.5, h * 0.24, d * 0.5), 0, headY, 0),
              translate(cylinder(r * 0.5, h * 0.3, { seg: 8 }), 0, cy - h * 0.3, 0),
            )
          : kind === 'meter'
            ? merge(
                translate(cylinderX(Math.min(h, d) * 0.3, w * 0.4, 16), 0, cy, 0),
                translate(boxCentered(w * 0.4, h * 0.22, d * 0.4), 0, headY * 0.92, 0),
              )
            : merge(
                translate(boxCentered(w * 0.6, h * 0.22, d * 0.5), 0, headY, 0),
                translate(cylinder(r * 0.4, h * 0.4, { seg: 8 }), 0, cy - h * 0.36, 0),
              );
    return {
      parts: [
        p(translate(cylinderX(r, w * 0.9, 12), 0, cy, 0), 'pipe'),
        pm(
          'machine',
          translate(rotateZ(flange(r * 1.5, 1.6, 12), Math.PI / 2), -w * 0.45, cy, 0),
          translate(rotateZ(flange(r * 1.5, 1.6, 12), -Math.PI / 2), w * 0.45, cy, 0),
        ),
        p(translate(cylinder(r * 0.55, stemH, { seg: 8 }), 0, cy + r, 0), 'brass'),
        p(head, kind === 'dial' ? 'stainless' : 'painted'),
        ...(kind === 'dial'
          ? [p(translate(cylinderZ(Math.min(w, d) * 0.32, 1.2, 18), 0, headY, Math.min(w, d) * 0.11), 'screen')]
          : [p(translate(box(w * 0.3, h * 0.08, 1.2), 0, headY, d * 0.26), 'screen')]),
        p(translate(cylinder(r * 0.5, cy - r, { seg: 8 }), 0, 0, 0), 'galv'),
      ],
      height: headY + h * 0.16,
      ports: {
        W: { p: v3(-w * 0.48, cy, 0), n: v3(-1, 0, 0) },
        E: { p: v3(w * 0.48, cy, 0), n: v3(1, 0, 0) },
        N: { p: v3(0, headY + h * 0.16, 0), n: v3(0, 1, 0) },
        S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
      },
      footprint: rectFoot(w * 0.8, d * 0.7),
    };
  };
}

export const pgauge = instrument('dial');
export const tsensor = instrument('probe');
export const flowmeter = instrument('meter');
export const levelswitch = instrument('switch');

// ─── Utilities ───────────────────────────────────────────────────────────

/** Centrifugal pump — volute, back-pull-out spacer and motor on a baseplate. */
export const centpump: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, h) * 0.26;
  const cy = h * 0.42;
  const mu = motorUnit(w * 0.44, R * 0.8);
  return {
    parts: [
      p(box(w * 0.96, h * 0.1, d * 0.9), 'galv'),
      // Volute with an axial suction and a radial (upward) discharge.
      p(
        merge(
          translate(cylinderX(R, w * 0.2, 20), -w * 0.3, cy, 0),
          translate(cylinderX(R * 1.1, 2.4, 20), -w * 0.4, cy, 0),
        ),
        'cast',
      ),
      pm(
        'cast',
        translate(cylinderX(R * 0.55, w * 0.16, 14), -w * 0.46, cy, 0),
        translate(cylinder(R * 0.5, R * 1.1, { seg: 14 }), -w * 0.3, cy + R * 0.6, 0),
      ),
      pm(
        'machine',
        translate(rotateZ(flange(R * 0.8, 2, 14), Math.PI / 2), -w * 0.52, cy, 0),
        translate(flange(R * 0.72, 2, 14), -w * 0.3, cy + R * 1.7, 0),
        // Bearing pedestal + coupling guard between pump and motor.
        translate(boxCentered(w * 0.16, R * 1.4, d * 0.3), -w * 0.1, cy - R * 0.7, 0),
      ),
      p(translate(cylinderX(R * 0.7, w * 0.14, 12), -w * 0.06, cy, 0), 'safety'),
      p(translate(merge(mu.body, mu.fins), w * 0.22, cy, 0), 'motor'),
    ],
    height: cy + R * 1.9,
    ports: {
      W: { p: v3(-w * 0.54, cy, 0), n: v3(-1, 0, 0) },
      N: { p: v3(-w * 0.3, cy + R * 1.9, 0), n: v3(0, 1, 0) },
      E: { p: v3(w / 2, cy, 0), n: v3(1, 0, 0) },
      S: { p: v3(-w * 0.3, cy - R, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Vacuum pump — a skid-mounted package with a separator bottle. */
export const vacpump: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(h, d) * 0.24;
  const cy = h * 0.44;
  return {
    parts: [
      p(skid(w * 0.96, d * 0.94, h * 0.14), 'galv'),
      p(translate(cylinderX(R, w * 0.44, 18), -w * 0.16, cy, 0), 'cast'),
      p(translate(merge(motorUnit(w * 0.3, R * 0.72).body, motorUnit(w * 0.3, R * 0.72).fins), w * 0.28, cy, 0), 'motor'),
      p(
        merge(
          translate(cylinder(R * 0.62, h * 0.4, { seg: 14 }), -w * 0.34, h * 0.14, 0),
          translate(dome(R * 0.62, 12), -w * 0.34, h * 0.54, 0),
        ),
        'stainless',
      ),
      pm(
        'pipe',
        translate(cylinder(R * 0.24, h * 0.16, { seg: 10 }), -w * 0.34, h * 0.54 + R * 0.5, 0),
        translate(cylinderX(R * 0.22, w * 0.2, 10), -w * 0.26, cy + R * 0.7, 0),
      ),
      p(translate(box(w * 0.2, h * 0.24, 2), 0, h * 0.14, d / 2), 'panel'),
    ],
    height: h * 0.54 + R * 1.2,
    ports: {
      N: { p: v3(-w * 0.34, h * 0.54 + R * 1.2, 0), n: v3(0, 1, 0) },
      W: { p: v3(-w / 2, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w / 2, cy, 0), n: v3(1, 0, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Chiller — a packaged unit with a fan deck and a finned condenser. */
export const chiller: Builder = ({ w, h, d }): Model3 => {
  const bodyH = h * 0.68;
  const fans: Mesh[] = [];
  const blades: Mesh[] = [];
  for (let i = 0; i < 2; i++) {
    const x = -w * 0.22 + w * 0.44 * i;
    const fr = Math.min(w * 0.2, d * 0.32);
    fans.push(translate(cylinder(fr, h * 0.08, { seg: 18 }), x, bodyH, 0));
    for (let b = 0; b < 4; b++)
      blades.push(translate(rotateX(boxCentered(fr * 1.7, 1.2, fr * 0.5), (b * Math.PI) / 4), x, bodyH + h * 0.05, 0));
  }
  return {
    parts: [
      p(box(w, h * 0.06, d), 'galv'),
      p(translate(box(w * 0.96, bodyH, d * 0.94), 0, h * 0.06, 0), 'painted'),
      // Condenser fins down two faces.
      p(
        merge(
          translate(box(w * 0.9, bodyH * 0.62, 1.4), 0, h * 0.16, d * 0.47),
          translate(box(w * 0.9, bodyH * 0.62, 1.4), 0, h * 0.16, -d * 0.47),
        ),
        'machine',
      ),
      p(merge(...fans), 'machineDark'),
      p(merge(...blades), 'machine'),
      p(translate(box(w * 0.26, bodyH * 0.44, 2), -w * 0.3, h * 0.2, d * 0.48), 'panel'),
      p(translate(box(w * 0.18, bodyH * 0.12, 1.2), -w * 0.3, h * 0.42, d * 0.5), 'screen'),
      pm(
        'lagging',
        translate(cylinderZ(Math.min(w, d) * 0.07, d * 0.3, 12), w * 0.36, h * 0.2, -d * 0.6),
        translate(cylinderZ(Math.min(w, d) * 0.07, d * 0.3, 12), w * 0.22, h * 0.2, -d * 0.6),
      ),
    ],
    height: bodyH + h * 0.14,
    ports: {
      E: { p: v3(w / 2, h * 0.3, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, h * 0.3, 0), n: v3(-1, 0, 0) },
      N: { p: v3(0, bodyH + h * 0.14, 0), n: v3(0, 1, 0) },
      S: { p: v3(w * 0.29, h * 0.2, -d * 0.61), n: v3(0, 0, -1) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Air compressor — receiver tank with the compressor block and motor on top. */
export const compressor: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(h * 0.2, d * 0.36);
  const tankY = h * 0.16;
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(4, tankY, 4), -w * 0.32, tankY / 2, 0),
        translate(boxCentered(4, tankY, 4), w * 0.32, tankY / 2, 0),
      ),
      p(translate(cylinderX(R, w * 0.84, 18), 0, tankY + R, 0), 'painted'),
      p(
        merge(
          translate(rotateZ(dome(R, 14), -Math.PI / 2), w * 0.42, tankY + R, 0),
          translate(rotateZ(dome(R, 14), Math.PI / 2), -w * 0.42, tankY + R, 0),
        ),
        'painted',
      ),
      p(translate(box(w * 0.34, h * 0.24, d * 0.5), -w * 0.16, tankY + R * 2, 0), 'cast'),
      p(translate(merge(motorUnit(w * 0.3, R * 0.5).body, motorUnit(w * 0.3, R * 0.5).fins), w * 0.24, tankY + R * 2.5, 0), 'motor'),
      p(translate(box(w * 0.24, h * 0.16, 2), -w * 0.16, tankY + R * 2.24, d * 0.26), 'safety'),
      pm(
        'pipe',
        translate(cylinder(R * 0.16, h * 0.14, { seg: 10 }), w * 0.1, tankY + R * 2, 0),
        translate(cylinderX(R * 0.14, w * 0.3, 8), 0, tankY + R * 2.6, 0),
      ),
    ],
    height: tankY + R * 3,
    ports: {
      E: { p: v3(w / 2, tankY + R, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, tankY + R, 0), n: v3(-1, 0, 0) },
      N: { p: v3(w * 0.1, tankY + R * 3, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, tankY, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Motor — a standalone drive on a foot mounting. */
export const motor: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(h * 0.32, d * 0.36);
  const cy = h * 0.14 + R;
  const mu = motorUnit(w * 0.7, R);
  return {
    parts: [
      p(box(w * 0.86, h * 0.14, d * 0.7), 'machineDark'),
      p(translate(mu.body, 0, cy, 0), 'motor'),
      p(translate(mu.fins, 0, cy, 0), 'motor'),
      p(translate(cylinderX(R * 0.24, w * 0.2, 10), -w * 0.44, cy, 0), 'steel'),
      p(translate(boxCentered(w * 0.2, R * 0.4, d * 0.5), 0, cy + R * 1.2, 0), 'machineDark'),
    ],
    height: cy + R * 1.4,
    ports: {
      W: { p: v3(-w * 0.52, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.42, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + R * 1.4, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w * 0.9, d * 0.8),
  };
};

/** Control panel — a floor-standing cubicle with a fascia, HMI and beacon. */
export const controlpanel: Builder = ({ w, h, d }): Model3 => {
  const cab = controlBox(w * 0.94, h * 0.94, d * 0.9);
  return {
    parts: [
      p(box(w, h * 0.06, d), 'machineDark'),
      p(translate(cab.body, 0, h * 0.06, 0), 'panel'),
      p(translate(cab.face, 0, h * 0.06, 0), 'screen'),
      // Door split line + handle.
      p(translate(box(1, h * 0.8, 1.2), 0, h * 0.1, d * 0.45), 'machineDark'),
      p(translate(cylinderZ(1.6, 4, 8), w * 0.3, h * 0.5, d * 0.45), 'machine'),
      // Beacon stack on the roof.
      pm(
        'accent',
        translate(cylinder(w * 0.06, h * 0.05, { seg: 10 }), w * 0.28, h * 1.02, 0),
      ),
      p(translate(cylinder(w * 0.045, h * 0.05, { seg: 10 }), w * 0.28, h * 1.07, 0), 'safety'),
      p(translate(cylinder(w * 0.03, h * 0.06, { seg: 8 }), w * 0.28, h * 0.96, 0), 'machineDark'),
    ],
    height: h * 1.12,
    ports: {
      W: { p: v3(-w / 2, h * 0.5, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w / 2, h * 0.5, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, h * 1.12, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, d / 2), n: v3(0, 0, 1) },
    },
    footprint: rectFoot(w, d),
  };
};

/**
 * Gasketed plate heat exchanger — a heavy fixed frame plate carrying the port
 * connections, a corrugated plate pack clamped against it by the movable
 * pressure plate, tie bolts down both edges, and the carrying and guide bars
 * running back to the support column.
 *
 * This is the unit the 2D symbol actually draws: a rectangle ruled with plate
 * lines, which on a PFD means plate-and-frame, not shell-and-tube. The
 * shell-and-tube form is still built below as `shelltube` for the places a
 * plant genuinely has one — it is picked from the 3D Model list, which records
 * the choice on the shape instead of re-guessing it every frame.
 *
 * The four connections are the one place the model has to bend to the drawing.
 * A real single-pass unit brings all four nozzles out of the FIXED plate,
 * pointing the same way; the tool has exactly four ports, one per compass
 * direction, and a run has to arrive along its port's normal or it visibly
 * stabs through the metal. So the frame plate keeps the real port pattern —
 * two at the top, two at the bottom — with the top-outer nozzle facing out
 * along the axis (W), the top-inner one elbowed up (N), the bottom-outer one
 * elbowed down (S), and the fourth blanked off under a studded cover, which is
 * an ordinary sight on a frame plate. That leaves the far side of the machine
 * with nothing to dock onto, so the last connection sits on the PRESSURE plate
 * (E) — a real two-pass arrangement, and the only way a run arriving from that
 * side lands on a nozzle rather than on paintwork.
 */
export const heatex: Builder = ({ w, h, d }): Model3 => {
  const L = w; // overall length, along the pack axis
  const PW = d * 0.78; // plate width, across the machine
  const footH = Math.max(2, h * 0.055);
  const plateH = h - footH; // the frame plates stand on their feet
  // Along X: fixed plate, plate pack, pressure plate, an open span, the column.
  // The open span is what you see the carrying bar through, and it is where the
  // pressure plate travels to when the pack is opened up for cleaning.
  const ft = L * 0.1; // fixed frame plate thickness
  const packL = L * 0.42;
  const pt = L * 0.075; // pressure (movable) plate thickness
  const ct = L * 0.055; // support column thickness
  const x0 = -L / 2; // outer face of the fixed plate
  const xPack = x0 + ft;
  const xPress = xPack + packL;
  const xPressEnd = xPress + pt;
  const xCol = L / 2 - ct;

  const packW = PW * 0.82;
  const packH = plateH * 0.74;
  const packY = footH + plateH * 0.13;

  const noz = Math.min(PW, plateH) * 0.082;
  const stub = noz * 2;
  const zoff = packW * 0.34; // the port pairs, side by side
  const yTop = footH + plateH * 0.82;
  const yBot = footH + plateH * 0.18;
  const elbX = x0 - stub * 1.1; // where the two elbowed connections turn
  const topY = h + stub * 0.8;
  const botY = footH * 0.5; // the drop stops short of the floor, not through it

  // Tie bolts run outboard of the plate pack and just inside the plate edges,
  // which is where they pass through a real frame plate. Both clearances are
  // proportional, so a symbol stretched to any shape still clamps its pack
  // rather than driving a bolt through the middle of it.
  const tieR = noz * 0.42;
  const tieZ = PW * 0.48;
  const tieY = [0, 1, 2, 3, 4].map((i) => footH + plateH * (0.14 + 0.18 * i));

  // Plate edges. The pack is a stack of pressed plates, and what you see of
  // them from outside is the fine ribbing down its flanks — so they are drawn
  // as ribs proud of a lighter core rather than as hundreds of real plates.
  const finN = Math.max(8, Math.min(24, Math.round(packL / (noz * 0.55))));
  const fins: Mesh[] = [];
  for (let i = 0; i < finN; i++)
    fins.push(
      translate(
        box((packL / finN) * 0.42, packH, packW * 1.04),
        xPack + (packL * (i + 0.5)) / finN,
        packY,
        0,
      ),
    );

  /** A port on the frame plate: the raised collar and its ring of bolt studs. */
  const collar = (y: number, z: number): Mesh =>
    merge(
      translate(rotateZ(flange(noz * 1.75, ft * 0.35, 14), Math.PI / 2), x0, y, z),
      ...[0, 1, 2, 3].map((i) => {
        const a = (Math.PI / 2) * i + Math.PI / 4;
        return translate(
          rotateZ(cylinder(noz * 0.17, ft * 0.55, { seg: 5 }), Math.PI / 2),
          x0,
          y + Math.cos(a) * noz * 1.45,
          z + Math.sin(a) * noz * 1.45,
        );
      }),
    );

  return {
    parts: [
      // Bolt-down pads under the fixed plate and the support column.
      pm(
        'galv',
        translate(box(ft * 1.9, footH * 0.3, PW * 0.26), x0 + ft / 2, 0, PW * 0.42),
        translate(box(ft * 1.9, footH * 0.3, PW * 0.26), x0 + ft / 2, 0, -PW * 0.42),
        translate(box(ct * 2.6, footH * 0.3, PW * 0.34), xCol + ct / 2, 0, 0),
      ),
      // The painted frame: fixed plate, pressure plate, column and their feet.
      // The pressure plate hangs off the carrying bar, so it stops short of the
      // floor — it is the one part of the frame that has to be able to move.
      pm(
        'painted',
        translate(box(ft, plateH, PW), x0 + ft / 2, footH, 0),
        translate(box(pt, plateH * 0.92, PW * 0.97), xPress + pt / 2, footH + plateH * 0.05, 0),
        translate(box(ct, plateH, PW * 0.24), xCol + ct / 2, footH, 0),
        translate(box(ft * 1.25, footH, PW * 0.18), x0 + ft / 2, 0, PW * 0.42),
        translate(box(ft * 1.25, footH, PW * 0.18), x0 + ft / 2, 0, -PW * 0.42),
        translate(box(ct * 1.6, footH, PW * 0.3), xCol + ct / 2, 0, 0),
      ),
      // What shows between the plate edges is the gasket line, so the core the
      // ribs stand out from is the dark one.
      p(translate(box(packL, packH, packW), xPack + packL / 2, packY, 0), 'rubber'),
      // The carrying bar the pack and the pressure plate hang from, the guide
      // bar under them, the tie-bolt nuts, and the cover over the unused port.
      pm(
        'machine',
        translate(
          cylinderX(plateH * 0.05, L - ft * 0.5, 10),
          (x0 + ft * 0.5 + L / 2) / 2,
          footH + plateH * 0.95,
          0,
        ),
        translate(
          cylinderX(plateH * 0.045, L - ft * 0.5, 10),
          (x0 + ft * 0.5 + L / 2) / 2,
          footH + plateH * 0.06,
          0,
        ),
        ...tieY.flatMap((y) =>
          [1, -1].map((sz) =>
            translate(cylinderX(noz * 0.62, ft * 0.4, 6), x0 - ft * 0.2, y, sz * tieZ),
          ),
        ),
        translate(rotateZ(flange(noz * 1.6, ft * 0.5, 12), Math.PI / 2), x0 - ft * 0.3, yBot, zoff),
      ),
      pm('machineDark', ...fins),
      // Tie bolts, clamping the pack between the two plates.
      pm(
        'accent',
        ...tieY.flatMap((y) =>
          [1, -1].map((sz) =>
            translate(cylinderX(tieR, xPressEnd - x0, 8), (x0 + xPressEnd) / 2, y, sz * tieZ),
          ),
        ),
      ),
      // Four port collars on the fixed plate: two at the top, two at the
      // bottom, which is the pattern that makes a plate exchanger recognisable.
      pm(
        'stainless',
        collar(yTop, -zoff),
        collar(yTop, zoff),
        collar(yBot, -zoff),
        collar(yBot, zoff),
      ),
      // The connections. Two straight nozzles and two elbowed ones, so a run
      // can reach the machine from any of the four directions.
      pm(
        'pipe',
        nozzle(v3(x0, yTop, -zoff), v3(-1, 0, 0), noz, stub),
        nozzle(v3(xPressEnd, yBot, zoff), v3(1, 0, 0), noz, stub),
        tube(
          filletPath([v3(x0, yTop, zoff), v3(elbX, yTop, zoff), v3(elbX, topY, zoff)], noz * 1.4),
          noz,
          10,
        ),
        tube(
          filletPath([v3(x0, yBot, -zoff), v3(elbX, yBot, -zoff), v3(elbX, botY, -zoff)], noz * 1.4),
          noz,
          10,
        ),
      ),
      // Nameplate, the way every exchanger carries one.
      p(
        translate(box(ft * 0.2, plateH * 0.1, PW * 0.16), x0 - ft * 0.1, footH + plateH * 0.45, 0),
        'brass',
      ),
    ],
    height: topY,
    ports: {
      W: { p: v3(x0 - stub, yTop, -zoff), n: v3(-1, 0, 0) },
      E: { p: v3(xPressEnd + stub, yBot, zoff), n: v3(1, 0, 0) },
      N: { p: v3(elbX, topY, zoff), n: v3(0, 1, 0) },
      S: { p: v3(elbX, botY, -zoff), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(L, PW * 1.1),
  };
};

/**
 * Shell-and-tube heat exchanger — a lagged shell on saddles, with a bolted
 * channel at each end and four nozzles. Not what the 2D symbol draws, so it is
 * reached by pinning a shape to this model rather than by drawing one.
 *
 * The geometry is driven from the saddle height rather than the shell centre,
 * because the shell-side outlet points DOWN: sized the other way round it hung
 * below the saddles, and since `groundModel` lifts a model until its lowest
 * point rests on the floor, the whole exchanger ended up hovering six units up
 * on nothing with a nozzle poking at the ground.
 *
 * Four connections, which is what this exchanger has: shell side in at the top
 * and out at the bottom, tube side in and out through the channels at each end,
 * so a run arriving from either side lands on a real nozzle.
 */
export const shelltube: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(h, d) * 0.3;
  const noz = R * 0.26; // nozzle radius
  const stub = R * 0.62; // how far a nozzle stands off the shell
  // Saddles tall enough to clear the bottom nozzle and its flange.
  const saddleH = stub + noz * 1.6 + 3;
  const cy = saddleH + R;
  const shellL = w * 0.66;
  const capX = shellL / 2;
  const top = cy + R + stub;

  /** A radial nozzle on the shell: stub plus a raised face. */
  const radial = (x: number, up: boolean): Mesh => {
    const m = merge(cylinder(noz, stub, { seg: 12 }), translate(flange(noz * 1.7, 2, 12), 0, stub - 2, 0));
    if (!up) rotateX(m, Math.PI);
    return translate(m, x, cy + (up ? R : -R), 0);
  };
  /** An axial nozzle out of a channel cover, pointing along ±X. */
  const axial = (dir: 1 | -1): Mesh => {
    const m = merge(cylinder(noz, stub, { seg: 12 }), translate(flange(noz * 1.7, 2, 12), 0, stub - 2, 0));
    rotateZ(m, dir === 1 ? -Math.PI / 2 : Math.PI / 2);
    return translate(m, dir * (capX + R * 0.66), cy, 0);
  };

  return {
    parts: [
      // Saddles, standing on the floor where a support belongs.
      pm(
        'galv',
        ...[-1, 1].flatMap((sx) => [
          translate(boxCentered(w * 0.055, saddleH, R * 1.9), sx * w * 0.28, saddleH / 2, 0),
          translate(box(w * 0.1, 2.2, R * 2.2), sx * w * 0.28, 0, 0),
        ]),
      ),
      // Lagged shell: the insulation covers the barrel and stops short of the
      // girth flanges, which is where the cladding actually ends.
      p(translate(cylinderX(R, shellL, 22), 0, cy, 0), 'stainless'),
      p(translate(cylinderX(R * 1.1, shellL * 0.82, 22), 0, cy, 0), 'lagging'),
      // Girth flanges and the bolted channel covers each end.
      pm(
        'machine',
        ...[-1, 1].map((sx) =>
          translate(rotateZ(flange(R * 1.18, 2.6, 20), (sx * Math.PI) / 2), sx * capX, cy, 0),
        ),
        ...[-1, 1].map((sx) =>
          translate(rotateZ(flange(R * 1.18, 2.6, 20), (sx * Math.PI) / 2), sx * (capX + R * 0.5), cy, 0),
        ),
      ),
      p(
        merge(
          ...[-1, 1].map((sx) =>
            translate(rotateZ(dome(R * 0.98, 16), (-sx * Math.PI) / 2), sx * (capX + R * 0.5), cy, 0),
          ),
        ),
        'stainless',
      ),
      // Four nozzles: shell side top and bottom, tube side through the channels.
      pm('pipe', radial(-shellL * 0.3, true), radial(shellL * 0.3, false), axial(1), axial(-1)),
      // Nameplate on the shell, the way every vessel carries one.
      p(translate(box(w * 0.1, R * 0.34, 1), 0, cy + R * 0.3, R * 1.12), 'brass'),
    ],
    height: top,
    ports: {
      // Tube side through the channels, shell side top and bottom.
      W: { p: v3(-(capX + R * 0.66 + stub), cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(capX + R * 0.66 + stub, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(-shellL * 0.3, cy + R + stub, 0), n: v3(0, 1, 0) },
      S: { p: v3(shellL * 0.3, cy - R - stub, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, R * 2.4),
  };
};

/** Weigh batcher — a load-celled hopper over a discharge gate. */
export const weighbatcher: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) * 0.36;
  const frameH = h * 0.3;
  const coneH = h * 0.3;
  const bodyH = h * 0.3;
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(3.2, frameH, 3.2), w / 2 - 4, frameH / 2, d / 2 - 4),
        translate(boxCentered(3.2, frameH, 3.2), -(w / 2 - 4), frameH / 2, d / 2 - 4),
        translate(boxCentered(3.2, frameH, 3.2), w / 2 - 4, frameH / 2, -(d / 2 - 4)),
        translate(boxCentered(3.2, frameH, 3.2), -(w / 2 - 4), frameH / 2, -(d / 2 - 4)),
        translate(box(w * 0.92, 2.4, d * 0.92), 0, frameH, 0),
      ),
      p(
        merge(
          translate(cylinder(2.6, h * 0.05, { seg: 10 }), w * 0.28, frameH + 2.4, d * 0.28),
          translate(cylinder(2.6, h * 0.05, { seg: 10 }), -w * 0.28, frameH + 2.4, d * 0.28),
          translate(cylinder(2.6, h * 0.05, { seg: 10 }), 0, frameH + 2.4, -d * 0.3),
        ),
        'accent',
      ),
      p(
        merge(
          translate(frustum(R * 0.26, R, coneH, { seg: 18, capTop: false, capBottom: false }), 0, frameH + h * 0.09, 0),
          translate(cylinder(R, bodyH, { seg: 18, capBottom: false }), 0, frameH + h * 0.09 + coneH, 0),
        ),
        'stainless',
      ),
      p(translate(cylinder(R * 0.3, h * 0.1, { seg: 12 }), 0, frameH * 0.5, 0), 'stainless'),
      p(translate(boxCentered(R * 1.2, h * 0.05, R * 0.4), R * 0.6, frameH * 0.5, 0), 'accent'),
      p(translate(box(w * 0.2, h * 0.14, 2), -w * 0.3, frameH * 0.3, d / 2), 'screen'),
    ],
    height: frameH + h * 0.09 + coneH + bodyH,
    ports: {
      N: { p: v3(0, frameH + h * 0.09 + coneH + bodyH, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, frameH * 0.5, 0), n: v3(0, -1, 0) },
      E: { p: v3(R, frameH + h * 0.09 + coneH + bodyH * 0.5, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R, frameH + h * 0.09 + coneH + bodyH * 0.5, 0), n: v3(-1, 0, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Air filter — a cartridge housing with a bowl and a differential gauge. */
export const airfilter: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) * 0.4;
  const cy = h * 0.62;
  return {
    parts: [
      p(translate(cylinderX(R * 0.5, w * 1.0, 12), 0, cy, 0), 'pipe'),
      pm(
        'machine',
        translate(rotateZ(flange(R * 0.8, 1.6, 12), Math.PI / 2), -w * 0.5, cy, 0),
        translate(rotateZ(flange(R * 0.8, 1.6, 12), -Math.PI / 2), w * 0.5, cy, 0),
        translate(cylinder(R, h * 0.16, { seg: 16 }), 0, cy - h * 0.08, 0),
      ),
      p(translate(frustum(R * 0.9, R * 0.7, h * 0.34, { seg: 16 }), 0, cy - h * 0.42, 0), 'glass'),
      p(translate(cylinder(R * 0.5, h * 0.28, { seg: 12 }), 0, cy - h * 0.38, 0), 'plastic'),
      p(translate(cylinderZ(R * 0.34, R * 0.3, 14), 0, cy + h * 0.14, -R * 0.5), 'stainless'),
      p(translate(cylinder(R * 0.2, h * 0.06, { seg: 8 }), 0, cy - h * 0.46, 0), 'brass'),
      // Floor stand — without it the housing hangs off its own pipework.
      p(translate(cylinder(R * 0.26, cy - h * 0.46, { seg: 8 }), 0, 0, 0), 'galv'),
    ],
    height: cy + h * 0.3,
    ports: {
      W: { p: v3(-w * 0.5, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.5, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + h * 0.3, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, cy - h * 0.5, 0), n: v3(0, -1, 0) },
    },
    footprint: roundFoot(R, 10),
  };
};

// ─── Annotations ─────────────────────────────────────────────────────────
//
// These are drawing furniture, not equipment. They render as low, translucent
// ground markings so they organise the scene without cluttering it — a zone box
// becomes a painted floor area, exactly as it would be in a real plant.

/**
 * Zone box — the drawing's way of saying "this area is X".
 *
 * Three forms, because one flat rectangle cannot say everything a layout needs
 * to say:
 *
 *   • `floor` paints the area on the ground with a kerb round it. The default,
 *     and what a zone box means on the 2D sheet.
 *   • `wall` stands a single panel up on one edge. That is what a tower face or
 *     a building elevation actually is, and it is what a vertical run of pipe
 *     needs to be seen against — the pipe reads as running DOWN something
 *     instead of hanging in mid-air.
 *   • `room` puts a low wall right round the area, for an enclosure.
 *
 * The variant string carries the form, the edge and the height, so each is
 * built and cached separately.
 */
export const zonebox: Builder = ({ w, h, d, variant }): Model3 => {
  const [mode, side, hStr] = (variant ?? 'floor||').split('|');
  const wallH = Math.max(20, parseFloat(hStr) || h * 3);

  if (mode === 'wall') {
    // Panel on one edge, standing up. Thin, so it reads as a face rather than
    // a slab, with a frame that gives it an edge to catch the light.
    const vertical = side === 'E' || side === 'W';
    const len = vertical ? d : w;
    const at = side === 'N' ? -d / 2 : side === 'S' ? d / 2 : side === 'W' ? -w / 2 : w / 2;
    const t = 1.6;
    const panel = vertical
      ? translate(box(t, wallH, len), at, 0, 0)
      : translate(box(len, wallH, t), 0, 0, at);
    const post = (u: number) =>
      vertical
        ? translate(boxCentered(t * 2.2, wallH, t * 2.2), at, wallH / 2, u)
        : translate(boxCentered(t * 2.2, wallH, t * 2.2), u, wallH / 2, at);
    const cap = vertical
      ? translate(box(t * 2.4, 2.6, len), at, wallH, 0)
      : translate(box(len, 2.6, t * 2.4), 0, wallH, at);
    // A couple of intermediate mullions so a tall face doesn't read as a sheet.
    const mullions: Mesh[] = [];
    for (const f of [-0.2, 0.2])
      mullions.push(post(len * f));
    return {
      parts: [
        p(panel, 'wallPanel'),
        pm('galv', post(-len / 2), post(len / 2), cap, merge(...mullions)),
      ],
      height: wallH,
      ports: {},
      footprint: vertical ? rectFoot(t * 3, len) : rectFoot(len, t * 3),
    };
  }

  if (mode === 'room') {
    const t = 1.6;
    const rh = Math.max(16, Math.min(wallH, h * 2));
    return {
      parts: [
        p(box(w, 0.4, d), 'zone'),
        pm(
          'wallPanel',
          translate(box(w, rh, t), 0, 0, -(d / 2 - t / 2)),
          translate(box(w, rh, t), 0, 0, d / 2 - t / 2),
          translate(box(t, rh, d), -(w / 2 - t / 2), 0, 0),
          translate(box(t, rh, d), w / 2 - t / 2, 0, 0),
        ),
        pm(
          'galv',
          translate(box(w, 2.2, t * 2), 0, rh, -(d / 2 - t)),
          translate(box(w, 2.2, t * 2), 0, rh, d / 2 - t),
          translate(box(t * 2, 2.2, d), -(w / 2 - t), rh, 0),
          translate(box(t * 2, 2.2, d), w / 2 - t, rh, 0),
        ),
      ],
      height: rh + 2.2,
      ports: {},
      footprint: rectFoot(w, d),
    };
  }

  return {
    parts: [
      p(box(w, 0.4, d), 'zone'),
      pm(
        'safety',
        translate(box(w, 1.2, 1.4), 0, 0, d / 2 - 0.7),
        translate(box(w, 1.2, 1.4), 0, 0, -(d / 2 - 0.7)),
        translate(box(1.4, 1.2, d), w / 2 - 0.7, 0, 0),
        translate(box(1.4, 1.2, d), -(w / 2 - 0.7), 0, 0),
      ),
    ],
    height: 1.2,
    ports: {},
    footprint: [],
  };
};

/** Text label — a small floor plate the label text is drawn above. */
export const textlabel: Builder = ({ w, d }): Model3 => ({
  parts: [p(box(w * 0.9, 0.6, d * 0.7), 'zone')],
  height: 0.6,
  ports: {},
  footprint: [],
});

/**
 * Process arrow — a floor-painted directional arrow.
 * Extruded from its own outline rather than assembled from a bar and a cone:
 * the outline keeps it genuinely FLAT, which a rotated cone never is, and a
 * flat arrow is the whole point of a floor marking.
 */
export const processarrow: Builder = ({ w, h, d }): Model3 => {
  const half = d * 0.2;
  const headW = Math.min(w * 0.36, d * 0.9);
  const x = w / 2;
  return {
    parts: [
      p(
        extrude(
          [
            { x: -x, y: -half },
            { x: x - headW, y: -half },
            { x: x - headW, y: -half * 2.1 },
            { x, y: 0 },
            { x: x - headW, y: half * 2.1 },
            { x: x - headW, y: half },
            { x: -x, y: half },
          ],
          Math.max(0.8, h * 0.03),
        ),
        'accent',
      ),
    ],
    height: Math.max(0.8, h * 0.03),
    ports: {},
    footprint: [],
  };
};

/** Title block — a leaning drawing board on a stand, at the sheet corner. */
export const titleblock: Builder = ({ w, h, d }): Model3 => ({
  parts: [
    p(box(w * 0.1, h * 0.5, d * 0.5), 'machineDark'),
    p(translate(rotateX(box(w, 1.2, h * 0.9), -0.5), 0, h * 0.5, 0), 'plastic'),
  ],
  height: h * 0.9,
  ports: {},
  footprint: rectFoot(w, d * 0.6),
});
